import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, AlertTriangleIcon, CheckCircle2Icon, Loader2Icon, RotateCcwIcon } from 'lucide-react';
import { flowDepartingUserId, orgUsers } from '../../data/offboarding';
import type { FlowOutcome } from '../../types/offboarding';
import { neutralButton, primaryButton } from '../../utils/buttonStyles';
import { ToolCardHeader } from './ToolCardHeader';
type FlowStatus = 'idle' | 'running' | 'complete' | 'finished' | 'cancelled';
interface FlowOffboardingProps {
  outcome: FlowOutcome;
}
const departingUser = orgUsers.find(u => u.id === flowDepartingUserId);
export function FlowOffboarding({
  outcome
}: FlowOffboardingProps) {
  const [status, setStatus] = useState<FlowStatus>('idle');
  const [validation, setValidation] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  // Changing the outcome in Controls restarts the step
  useEffect(() => {
    clearTimeout(timer.current);
    setStatus('idle');
    setValidation(null);
  }, [outcome]);
  const proceed = () => {
    if (!departingUser || status === 'running') return;
    setValidation(null);
    setStatus('running');
    timer.current = setTimeout(() => setStatus('complete'), 2200);
  };
  const finish = () => {
    if (status === 'idle') {
      setValidation('Click Proceed Offboarding to run borrower account reassignment before continuing.');
      return;
    }
    if (status === 'running') {
      setValidation('Borrower account reassignment is still running. Wait for the summary, then click Finish.');
      return;
    }
    setStatus('finished');
  };
  const reset = () => {
    setStatus('idle');
    setValidation(null);
  };
  const isComplete = status === 'complete';
  const name = departingUser?.name ?? 'the selected user';
  return <section aria-label="User Offboarding Tool — termination flow step" className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-105" data-mp="2k2oftrxnmjyz">
      <ToolCardHeader subtitle="Borrower reassignment before termination" badge="Termination flow" data-id="element-106" data-mp="1movaq3ixvuav" />

      {status === 'finished' || status === 'cancelled' ? <div className="px-5 py-10 text-center sm:px-6" data-id="element-107" data-mp="1qovmfgenlre8">
          <p className="text-sm font-medium text-gray-900" data-id="element-108" data-mp="1vp08dlybzebu">
            {status === 'finished' ? 'Borrower step complete. The termination flow will continue.' : 'Returned to the previous flow screen.'}
          </p>
          <button type="button" onClick={reset} className={`${neutralButton} mt-5`} data-id="element-109" data-mp="12z1d38tjmkyq">
            <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-110" data-mp="17z5vx5hxbrs4" />
            Restart this step
          </button>
        </div> : <>
          <div className="space-y-5 px-5 py-6 sm:px-6" data-id="element-111" data-mp="fcl5q8d6plq5">
            {departingUser && <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3" data-id="element-112" data-mp="kae80qbd9vl5">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-700 ring-1 ring-gray-200" data-id="element-113" data-mp="crn66puz3xjb">
                  {departingUser.name.split(' ').map(p => p[0]).join('')}
                </span>
                <div className="min-w-0 flex-1" data-id="element-114" data-mp="3ux04s8570yu6">
                  <p className="truncate text-sm font-semibold text-gray-900" data-id="element-115" data-mp="3uw896xblj13w">{departingUser.name}</p>
                  <p className="truncate text-xs text-gray-500" data-id="element-116" data-mp="3pzhtwc7m89jj">{departingUser.email}</p>
                </div>
                <span className="flex-shrink-0 text-[11px] font-medium text-gray-500" data-id="element-117" data-mp="3l5vqlancdi0e">
                  Departing user
                </span>
              </div>}

            <div className="space-y-2 text-sm text-gray-700" data-id="element-118" data-mp="3ba28lbquswb8">
              <p data-id="element-119" data-mp="3775unyc9dodm">
                This step reassigns borrower accounts for the selected user before termination
                continues.
              </p>
              <p data-id="element-120" data-mp="3294b7cax7n1m">
                Click <span className="font-semibold text-gray-900" data-id="element-121" data-mp="32aow0a7fnrma">Proceed Offboarding</span> to run
                borrower account reassignment, review the summary, then click{' '}
                <span className="font-semibold text-gray-900" data-id="element-122" data-mp="1a5zw9brcn7w0">Finish</span>.
              </p>
            </div>

            <div aria-live="polite" data-id="element-123" data-mp="wda8p2w69uws">
              <AnimatePresence mode="wait" initial={false} data-id="element-124" data-mp="11dxy5k4r63ow">
                {status === 'running' && <StatusPanel key="running" data-id="element-125" data-mp="mgxi4dvxbx69">
                    <div className="flex items-center gap-2.5 text-sm text-gray-700" data-id="element-126" data-mp="mjbc0jevb3c8">
                      <Loader2Icon className="h-4 w-4 animate-spin text-navy-600" aria-hidden="true" data-id="element-127" data-mp="rhl8qyg8qbzy" />
                      Running borrower reassignment…
                    </div>
                  </StatusPanel>}
                {isComplete && outcome === 'success' && <StatusPanel key="success" tone="success" data-id="element-128" data-mp="27v30qfj29yzg">
                    <div className="flex gap-3" data-id="element-129" data-mp="2cu67q5ilqfa2">
                      <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" aria-hidden="true" data-id="element-130" data-mp="2csv2e19iczgu" />
                      <div className="space-y-0.5 text-sm" data-id="element-131" data-mp="2mv74lkzae376">
                        <p className="font-semibold text-gray-900" data-id="element-132" data-mp="2hzrsaxgxgfcv">
                          Borrower account reassignment complete.
                        </p>
                        <p className="text-gray-700" data-id="element-133" data-mp="2qzeu3kdkjn6g">3 accounts reassigned to fallback owner.</p>
                        <p className="text-gray-700" data-id="element-134" data-mp="35nsvrrz915lf">0 accounts require manual follow-up.</p>
                      </div>
                    </div>
                  </StatusPanel>}
                {isComplete && outcome === 'followUp' && <StatusPanel key="followUp" tone="danger" data-id="element-135" data-mp="3eo7mj6uzfcwb">
                    <div className="flex gap-3" data-id="element-136" data-mp="3amw2dguvnjvi">
                      <AlertTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-danger" aria-hidden="true" data-id="element-137" data-mp="3al1fhx01phos" />
                      <div className="space-y-0.5 text-sm" data-id="element-138" data-mp="1kcjabcg4xofm">
                        <p className="font-semibold text-danger" data-id="element-139" data-mp="1gapf3joplcmu">Follow-up required</p>
                        <p className="text-gray-700" data-id="element-140" data-mp="3fexu6yr4d5ek">
                          2 borrower accounts could not be automatically reassigned.
                        </p>
                        <p className="text-gray-700" data-id="element-141" data-mp="3pcc0i5jbwdt5">
                          Review accounts owned by {name} and reassign manually.
                        </p>
                      </div>
                    </div>
                  </StatusPanel>}
              </AnimatePresence>

              {validation && <p role="alert" className="mt-3 flex items-start gap-1.5 text-xs font-medium text-danger" data-id="element-142" data-mp="75e6ywivtrjw">
                  <AlertCircleIcon className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" data-id="element-143" data-mp="byh0j4nnt103" />
                  {validation}
                </p>}
            </div>
          </div>

          <footer className="flex flex-col gap-3 border-t border-gray-100 px-5 py-5 sm:px-6 md:flex-row md:justify-end" data-id="element-144" data-mp="lwn1nk1n633w">
            {!isComplete && <>
                <button type="button" onClick={() => setStatus('cancelled')} className={neutralButton} data-id="element-145" data-mp="2afhjikncz4tu">
                  Cancel
                </button>
                <button type="button" onClick={finish} aria-disabled="true" className={`${neutralButton} text-gray-400 hover:text-gray-500`} data-id="element-146" data-mp="26fh3ivkm2lko">
                  Finish
                </button>
                <button type="button" onClick={proceed} disabled={!departingUser || status === 'running'} className={primaryButton} data-id="element-147" data-mp="2u2fj7r0wpphj">
                  Proceed Offboarding
                </button>
              </>}
            {isComplete && <button type="button" onClick={finish} className={`${primaryButton} md:min-w-[120px]`} data-id="element-148" data-mp="3ct6mqby7qlo0">
                Finish
              </button>}
          </footer>
        </>}
    </section>;
}
function StatusPanel({
  tone = 'neutral',
  children
}: {
  tone?: 'neutral' | 'success' | 'danger';
  children: React.ReactNode;
}) {
  const toneClass = tone === 'success' ? 'border-success/25 bg-success/5' : tone === 'danger' ? 'border-danger/25 bg-danger/5' : 'border-gray-200 bg-[#F9FAFB]';
  return <motion.div initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} className={`rounded-xl border px-4 py-3.5 ${toneClass}`} data-id="element-149" data-mp="2bglnz7l3uddy">
      {children}
    </motion.div>;
}